import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { verifyEmail, resendVerificationEmail } from '../services/authApiClient';
import toast from 'react-hot-toast';

const VerifyEmailPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'expired'>('verifying');
    const [message, setMessage] = useState('Verifying your email...');
    const [email, setEmail] = useState('');
    const [isResending, setIsResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);

    const verificationAttempted = React.useRef(false);

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setMessage('Invalid verification link. No token provided.');
            return;
        }

        if (verificationAttempted.current) return;
        verificationAttempted.current = true;

        const verify = async () => {
            try {
                await verifyEmail(token);
                setStatus('success');
                setMessage('Email verified successfully! You can now log in.');

                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } catch (err: any) {
                console.error('Verification error:', err);
                // Check if it's an expired token
                if (err.message?.toLowerCase().includes('expired')) {
                    setStatus('expired');
                    setMessage('This verification link has expired. Please request a new one.');
                } else {
                    setStatus('error');
                    setMessage(err.message || 'Verification failed. The link may be invalid or already used.');
                }
            }
        };

        verify();
    }, [token, navigate]);

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleResend = async () => {
        if (!email || isResending || resendCooldown > 0) return;

        setIsResending(true);
        try {
            await resendVerificationEmail(email);
            toast.success('Verification email sent! Check your inbox.');
            setResendCooldown(60);
        } catch (err: any) {
            toast.error(err.message || 'Failed to send email');
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-md w-full bg-gray-800 rounded-xl p-8 shadow-2xl border border-gray-700 text-center"
            >
                <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                    {status === 'verifying' && (
                        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    )}
                    {status === 'success' && (
                        <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                    {status === 'error' && (
                        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    )}
                    {status === 'expired' && (
                        <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    )}
                </div>

                <h2 className="text-2xl font-bold text-white mb-2">
                    {status === 'verifying' && 'Verifying Email...'}
                    {status === 'success' && 'Email Verified!'}
                    {status === 'error' && 'Verification Failed'}
                    {status === 'expired' && 'Link Expired'}
                </h2>

                <p className="text-gray-400 mb-6">
                    {message}
                </p>

                {status === 'success' && (
                    <p className="text-sm text-gray-500">Redirecting to login...</p>
                )}

                {(status === 'error' || status === 'expired') && (
                    <div className="space-y-4">
                        {status === 'expired' && (
                            <div className="bg-gray-700/50 rounded-lg p-4 text-left">
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Enter your email to request a new link:
                                </label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-teal-500 mb-3"
                                />
                                <button
                                    onClick={handleResend}
                                    disabled={!email || isResending || resendCooldown > 0}
                                    className={`w-full py-2 rounded-lg font-medium transition-colors ${!email || isResending || resendCooldown > 0
                                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                            : 'bg-teal-600 hover:bg-teal-700 text-white'
                                        }`}
                                >
                                    {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Send New Link'}
                                </button>
                            </div>
                        )}

                        <button
                            onClick={() => navigate('/login')}
                            className="py-2 px-4 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                        >
                            Return to Login
                        </button>
                    </div>
                )}
            </motion.div>

            {/* Background Overlay similar to other auth pages */}
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat -z-10 opacity-20"
                style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80)'
                }}
            />
        </div>
    );
};

export default VerifyEmailPage;

